/**
 * AdminTopnav — B 端後台「深藍滿版頂部 NAV」控制（取代 admin-sidebar.js）
 *
 * 載入：由 /src/admin.js 中央 import 鏈載入（後台 / auth / member 頁殼）。
 *
 * 三個獨立關注點，全 auto-init、無對應 DOM 即 no-op：
 *
 *   1. 行動頂部面板（≤1024 漢堡開）：[data-topnav-hamburger] 觸發
 *      - backdrop click 關 / Esc 關 / focus-trap（面板內 Tab 循環）/ 焦點返還觸發鈕
 *      - ≥1025 強制關（viewport 跨界）
 *
 *   2. dropdown（身分 cluster + platform 分組，多實例）：[data-topnav-dropdown]
 *      - trigger click 開合 / 點外關 / Esc 關（焦點返還 trigger）
 *      - 同時間只開一個（開新的先關其他）
 *      - aria-expanded 同步 trigger；面板 hidden attr + .is-open class 雙軌
 *        （hidden 控 display 顯隱、is-open 控進場 opacity/transform 過場）
 *
 * 沿用 admin-sidebar.js 既有 focus-trap / backdrop / Esc / aria 模式。
 * 動態全在 CSS（admin-topnav.css），JS 只切 class / attr / 管焦點。
 */

const MOBILE_MAX = 1024;

// prefersReducedMotion 收進共用 util（原 inline 同式收斂，import 為 ESM hoisted）
import { prefersReducedMotion } from '../utils/dom.js';
// focus-trap selector + getFocusable + Tab 循環 → 收斂至 utils/focus-trap.js（單一真相源）。
// 行為變更（皆 a11y 強化，刻意統一）：
//   1. getFocusable filter 改為 keep-active-el-if-hidden（原僅 offsetParent!==null，會在焦點控制項
//      過場算 hidden 時把它丟掉 → canonical 保留當前 activeElement，避免焦點流失）。
//   2. trapTab 帶「焦點逃出 panel 即拉回」逃逸復原守衛（原 inline 版無）。
import { getFocusable, trapTab } from '../utils/focus-trap.js';

/* =====================================================================
 * 1. 行動頂部面板（漢堡）
 * ===================================================================== */
class PanelController {
  constructor(panel) {
    this.panel = panel;
    this.backdrop = document.querySelector('[data-topnav-panel-backdrop]');
    this.triggers = [];
    this.previousFocus = null;
    this.isOpen = false;
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdrop = () => this.close();
  }

  registerTrigger(trigger) {
    if (!trigger || this.triggers.indexOf(trigger) !== -1) return;
    this.triggers.push(trigger);
  }

  _syncTriggers(open) {
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.previousFocus = document.activeElement;

    if (this.backdrop) this.backdrop.hidden = false;
    this.panel.hidden = false;
    // 強制 reflow 讓 hidden→顯示後的進場 transition 生效（先 paint 前態，再加 is-open）
    void this.panel.offsetWidth;
    this.panel.classList.add('is-open');
    if (this.backdrop) this.backdrop.classList.add('is-open');
    this._syncTriggers(true);

    document.documentElement.classList.add('admin-topnav-panel-open');
    document.addEventListener('keydown', this._onKeydown);
    if (this.backdrop) this.backdrop.addEventListener('click', this._onBackdrop);

    const focusables = getFocusable(this.panel);
    const target = focusables[0] || this.panel;
    if (target === this.panel && !this.panel.hasAttribute('tabindex')) {
      this.panel.setAttribute('tabindex', '-1');
    }
    setTimeout(() => target.focus(), prefersReducedMotion() ? 0 : 60);
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.panel.classList.remove('is-open');
    if (this.backdrop) this.backdrop.classList.remove('is-open');
    this._syncTriggers(false);

    document.documentElement.classList.remove('admin-topnav-panel-open');
    document.removeEventListener('keydown', this._onKeydown);
    if (this.backdrop) this.backdrop.removeEventListener('click', this._onBackdrop);

    const finish = () => {
      // 動畫收尾後才 hidden（避免過場被 display:none 截斷）；reduced-motion 立即
      this.panel.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
    };
    if (prefersReducedMotion()) {
      finish();
    } else {
      const onEnd = (e) => {
        if (e.target !== this.panel) return;
        this.panel.removeEventListener('transitionend', onEnd);
        finish();
      };
      this.panel.addEventListener('transitionend', onEnd);
      // 保底：transitionend 漏觸發（display 競態）時定時兜底
      setTimeout(() => {
        if (!this.isOpen && !this.panel.hidden) finish();
      }, 400);
    }

    if (restoreFocus && this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
    this.previousFocus = null;
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      trapTab(e, this.panel);
    }
  }
}

/* =====================================================================
 * 2. dropdown（身分 + platform 分組，多實例）
 * ===================================================================== */
const openDropdowns = new Set();

class DropdownController {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-topnav-dropdown-trigger]');
    this.panel = root.querySelector('[data-topnav-dropdown-panel]');
    this.isOpen = false;
    this._onKeydown = this._onKeydown.bind(this);

    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
  }

  open() {
    if (this.isOpen || !this.panel) return;
    // 同時間只開一個：先關其他
    openDropdowns.forEach((d) => { if (d !== this) d.close({ restoreFocus: false }); });

    this.isOpen = true;
    openDropdowns.add(this);

    this.panel.hidden = false;
    void this.panel.offsetWidth;
    this.panel.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', this._onKeydown);
    // 點外關：延遲掛載避免捕捉到本次開啟的 click
    setTimeout(() => document.addEventListener('click', this._onDocClick), 0);
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen || !this.panel) return;
    this.isOpen = false;
    openDropdowns.delete(this);

    this.panel.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');

    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onDocClick);

    const finish = () => { this.panel.hidden = true; };
    if (prefersReducedMotion()) {
      finish();
    } else {
      const onEnd = (e) => {
        if (e.target !== this.panel) return;
        this.panel.removeEventListener('transitionend', onEnd);
        finish();
      };
      this.panel.addEventListener('transitionend', onEnd);
      setTimeout(() => { if (!this.isOpen && !this.panel.hidden) finish(); }, 200);
    }

    if (restoreFocus && this.trigger && typeof this.trigger.focus === 'function') {
      this.trigger.focus();
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // 箭頭函式綁定 this，供 add/removeEventListener 引用同一 reference
  _onDocClick = (e) => {
    if (!this.root.contains(e.target)) this.close({ restoreFocus: false });
  };

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.close();
    }
  }
}

/* =====================================================================
 * Auto-init
 * ===================================================================== */
let panelController = null;

function bindAll(root = document) {
  // 1) 行動面板（單一）
  const panelEl = root.querySelector('[data-topnav-panel]');
  if (panelEl && !panelController) {
    panelController = new PanelController(panelEl);
  }
  if (panelController) {
    root.querySelectorAll('[data-topnav-hamburger]').forEach((trigger) => {
      if (trigger.dataset.topnavBound === '1') return;
      trigger.dataset.topnavBound = '1';
      panelController.registerTrigger(trigger);
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        panelController.isOpen ? panelController.close() : panelController.open();
      });
    });
  }

  // 2) dropdowns（多實例）
  root.querySelectorAll('[data-topnav-dropdown]').forEach((el) => {
    if (el.dataset.topnavDropdownBound === '1') return;
    el.dataset.topnavDropdownBound = '1';
    new DropdownController(el);
  });

  // 3) viewport 跨界：≥1025 強制關行動面板（桌機水平 nav 接管）
  if (!bindAll._mqlBound) {
    bindAll._mqlBound = true;
    const mql = window.matchMedia(`(min-width: ${MOBILE_MAX + 1}px)`);
    const onChange = () => {
      if (mql.matches && panelController && panelController.isOpen) {
        panelController.close({ restoreFocus: false });
      }
    };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindAll());
} else {
  bindAll();
}
